import React, { useRef, useEffect, useState } from 'react';
import * as PIXI from 'pixi.js';
import { gsap } from 'gsap';
import { useKeyboardInput } from '../hooks/useKeyboardInput';
import { useTouchInput } from '../hooks/useTouchInput';
import { audioManager } from '../utils/audioManager';
import { BloomEffect } from '../effects/bloomEffect';
import { EmojiBurstSystem } from '../effects/EmojiBurstSystem';
import { lerp, clamp, wrap, getFrameIndex, crossedFrame } from '../utils/lerp';
import { PHYSICS_CONFIG, FRAME_CONFIG } from '../config/physics';
import { FrameEventsData } from '../types';

/**
 * ============================================================================
 * TAPE DECK COMPONENT
 * ============================================================================
 * 
 * A premium interactive video scrubbing experience using:
 * - PixiJS for frame-perfect rendering
 * - GSAP Ticker for smooth animation loop
 * - Lerp-based physics for momentum
 * - Howler.js for synchronized audio
 * - Bloom filters for visual effects
 * 
 * USAGE:
 * <TapeDeck 
 *   framesPath="/frames"
 *   framePattern="frame_%04d.webp"
 *   totalFrames={300}
 *   eventsPath="/data/frame-events.json"
 * />
 */

import { YuMode } from './ModeSelector';

interface TapeDeckProps {
    /** Path to directory containing frame images */
    framesPath: string;

    /** Frame filename pattern (e.g., "frame_%04d.webp") */
    framePattern: string;

    /** Total number of frames */
    totalFrames: number;

    /** Path to frame events JSON file */
    eventsPath: string;

    /** Canvas width (default: window width) */
    width?: number;

    /** Canvas height (default: window height) */
    height?: number;

    /** Current Audio Mode */
    currentMode: YuMode;
}

export const TapeDeck: React.FC<TapeDeckProps> = ({
    framesPath,
    framePattern,
    totalFrames,
    eventsPath,
    width,
    height,
    currentMode,
}) => {
    // ============================================================================
    // REFS & STATE
    // ============================================================================

    const canvasRef = useRef<HTMLDivElement>(null);
    const appRef = useRef<PIXI.Application | null>(null);
    const texturesRef = useRef<PIXI.Texture[]>([]);
    const spriteRef = useRef<PIXI.Sprite | null>(null);
    const bloomRef = useRef<BloomEffect | null>(null);
    const emojiSystemRef = useRef<EmojiBurstSystem | null>(null);

    // Physics state
    const currentFrameRef = useRef<number>(0);
    const targetVelocityRef = useRef<number>(0);
    const currentVelocityRef = useRef<number>(0);
    const previousFrameRef = useRef<number>(0);

    // Frame events data
    const [frameEvents, setFrameEvents] = useState<FrameEventsData | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Keyboard input
    const keyState = useKeyboardInput();
    const keyStateRef = useRef(keyState);

    // Touch/Click input
    const touchState = useTouchInput(canvasRef);
    const touchStateRef = useRef(touchState);

    // Update keyStateRef when keyState changes
    useEffect(() => {
        keyStateRef.current = keyState;
    }, [keyState]);

    // Update touchStateRef when touchState changes
    useEffect(() => {
        touchStateRef.current = touchState;
    }, [touchState]);

    // Update Audio Mode
    useEffect(() => {
        audioManager.setMode(currentMode);
    }, [currentMode]);

    // Update Color Grading
    useEffect(() => {
        if (!spriteRef.current) return;

        const sprite = spriteRef.current;
        const colorMatrix = (sprite as any)._colorMatrix as PIXI.ColorMatrixFilter;

        if (!colorMatrix) return;

        // Reset
        colorMatrix.reset();
        sprite.tint = 0xFFFFFF;

        switch (currentMode) {
            case 'fruity':
                // Vibrant & Saturated
                colorMatrix.saturate(2, true);
                break;

            case 'melancholic':
                // Desaturated (Black & White)
                colorMatrix.desaturate();
                break;

            case 'mysterious':
                // Red Tint
                sprite.tint = 0xFF8888;
                break;

            case 'curious':
            default:
                // Natural (No change)
                break;
        }
    }, [currentMode, isLoading]);

    // ============================================================================
    // INITIALIZATION
    // ============================================================================

    useEffect(() => {
        // Load frame events data
        fetch(eventsPath)
            .then(res => res.json())
            .then((data: FrameEventsData) => {
                setFrameEvents(data);
                // Preload is no longer needed for Tone.js synthesis, but we keep the data load
            })
            .catch(err => console.error('[TapeDeck] Failed to load frame events:', err));
    }, [eventsPath]);

    // Initialize Audio on first interaction
    useEffect(() => {
        const initAudio = () => {
            audioManager.initialize();
            window.removeEventListener('keydown', initAudio);
            window.removeEventListener('click', initAudio);
        };

        window.addEventListener('keydown', initAudio);
        window.addEventListener('click', initAudio);

        return () => {
            window.removeEventListener('keydown', initAudio);
            window.removeEventListener('click', initAudio);
        };
    }, []);

    useEffect(() => {
        if (!canvasRef.current || !frameEvents) return;

        // Prevent double-initialization in React Strict Mode
        if (canvasRef.current.children.length > 0) {
            console.log('[TapeDeck] Canvas already initialized, skipping...');
            return;
        }

        // ============================================================================
        // PIXI APPLICATION SETUP
        // ============================================================================

        const app = new PIXI.Application({
            width: width || window.innerWidth,
            height: height || window.innerHeight,
            backgroundColor: 0x000000,
            antialias: false, // Disable antialiasing for sharp pixels
            resolution: window.devicePixelRatio || 1,
            autoDensity: true,
        });

        // Disable image smoothing on the canvas for crisp rendering
        const canvas = app.view as HTMLCanvasElement;
        canvas.style.imageRendering = 'pixelated';
        canvas.style.imageRendering = '-moz-crisp-edges';
        canvas.style.imageRendering = 'crisp-edges';

        canvasRef.current.appendChild(canvas);
        appRef.current = app;

        // ============================================================================
        // LOAD FRAME TEXTURES
        // ============================================================================

        const loadFrames = async () => {
            console.log('[TapeDeck] Loading frames...');
            const textures: PIXI.Texture[] = [];

            for (let i = 0; i < totalFrames; i++) {
                // Format frame number with padding (e.g., 0001, 0002, ...)
                const frameNum = String(i + 1).padStart(4, '0');
                const framePath = `${framesPath}/${framePattern.replace('%04d', frameNum)}`;

                try {
                    const texture = await PIXI.Texture.from(framePath);
                    // Set to nearest neighbor for sharp pixels (no blur)
                    texture.baseTexture.scaleMode = PIXI.SCALE_MODES.NEAREST;
                    textures.push(texture);
                } catch (err) {
                    console.error(`[TapeDeck] Failed to load frame ${i}:`, err);
                    // Create a placeholder texture on error
                    textures.push(PIXI.Texture.EMPTY);
                }
            }

            texturesRef.current = textures;
            console.log(`[TapeDeck] Loaded ${textures.length} frames`);

            // ============================================================================
            // CREATE SPRITE
            // ============================================================================

            console.log('[TapeDeck] Creating sprite...');
            const sprite = new PIXI.Sprite(textures[0]);

            // Set texture filtering to nearest for sharper images (no blur)
            textures.forEach(texture => {
                texture.baseTexture.scaleMode = PIXI.SCALE_MODES.NEAREST;
            });

            console.log('[TapeDeck] Sprite created');

            // Wait for texture to be ready before scaling
            if (textures[0].baseTexture.valid) {
                setupSprite();
            } else {
                textures[0].baseTexture.once('loaded', () => {
                    setupSprite();
                });
            }

            function setupSprite() {
                // Center sprite
                sprite.anchor.set(0.5);
                sprite.x = app.screen.width / 2;
                sprite.y = app.screen.height / 2;
                console.log('[TapeDeck] Sprite positioned');

                // Scale to fit screen while maintaining aspect ratio
                console.log(`[TapeDeck] Sprite dimensions: ${sprite.width}x${sprite.height}`);
                console.log(`[TapeDeck] Screen dimensions: ${app.screen.width}x${app.screen.height}`);

                // Only scale if sprite has valid dimensions
                if (sprite.width > 1 && sprite.height > 1) {
                    const scaleX = app.screen.width / sprite.width;
                    const scaleY = app.screen.height / sprite.height;
                    // Use min of calculated scale and 1.0 to never upscale (prevents blur)
                    const scale = Math.min(scaleX, scaleY, 1.0);

                    console.log(`[TapeDeck] Calculated scale: ${scale}`);
                    sprite.scale.set(scale);
                    console.log('[TapeDeck] Sprite scaled');
                } else {
                    console.error('[TapeDeck] Invalid sprite dimensions, using default scale');
                    sprite.scale.set(1);
                }

                spriteRef.current = sprite;

                // ============================================================================
                // BLOOM & COLOR EFFECTS
                // ============================================================================

                console.log('[TapeDeck] Creating effects...');
                const bloom = new BloomEffect();
                const colorMatrix = new PIXI.ColorMatrixFilter();

                // Combine filters
                sprite.filters = [bloom.getFilter(), colorMatrix];

                bloomRef.current = bloom;
                // Store color matrix ref if needed, or just access via sprite.filters? 
                // Better to keep a ref to the filter itself for updates.
                // We can attach it to the sprite for now.

                // We need to store the color matrix filter to update it later
                (sprite as any)._colorMatrix = colorMatrix;

                console.log('[TapeDeck] Effects created');

                // ... (rest of setup)



                // ... (rest of component)

                console.log('[TapeDeck] Adding sprite to stage...');
                app.stage.addChild(sprite);
                console.log('[TapeDeck] Sprite added to stage');

                // ============================================================================
                // EMOJI SYSTEM
                // ============================================================================

                console.log('[TapeDeck] Creating emoji system...');
                // Initialize AFTER adding sprite so emojis are on top
                const emojiSystem = new EmojiBurstSystem(app.stage);
                emojiSystemRef.current = emojiSystem;

                console.log('[TapeDeck] Setting isLoading to false...');
                setIsLoading(false);
                console.log('[TapeDeck] isLoading set to false');
            }
        };

        try {
            loadFrames();
        } catch (error) {
            console.error('[TapeDeck] Error in loadFrames:', error);
            setIsLoading(false);
        }

        // ============================================================================
        // GSAP TICKER LOOP
        // ============================================================================

        const ticker = gsap.ticker.add(() => {
            if (!spriteRef.current || texturesRef.current.length === 0) return;

            // Update Emoji System
            emojiSystemRef.current?.update();

            // ========================================================================
            // PHYSICS: Calculate Target Velocity
            // ========================================================================

            let targetVelocity = 0;

            // Check both keyboard and touch input
            const isDownPressed = keyStateRef.current.downPressed || touchStateRef.current.downPressed;
            const isUpPressed = keyStateRef.current.upPressed || touchStateRef.current.upPressed;

            if (isDownPressed) {
                // Forward playback
                targetVelocity = PHYSICS_CONFIG.maxVelocity;
            } else if (isUpPressed) {
                // Backward playback
                targetVelocity = -PHYSICS_CONFIG.maxVelocity;
            }

            targetVelocityRef.current = targetVelocity;

            // ========================================================================
            // PHYSICS: Lerp Current Velocity
            // ========================================================================

            if (targetVelocity !== 0) {
                // Accelerate toward target velocity
                currentVelocityRef.current = lerp(
                    currentVelocityRef.current,
                    targetVelocity,
                    PHYSICS_CONFIG.acceleration
                );
            } else {
                // Apply friction when no input
                currentVelocityRef.current *= PHYSICS_CONFIG.friction;

                // Stop completely when velocity is very small
                if (Math.abs(currentVelocityRef.current) < 0.001) {
                    currentVelocityRef.current = 0;
                }
            }

            // ========================================================================
            // UPDATE FRAME
            // ========================================================================

            previousFrameRef.current = currentFrameRef.current;
            currentFrameRef.current += currentVelocityRef.current;

            // Handle looping or clamping
            if (FRAME_CONFIG.loop) {
                currentFrameRef.current = wrap(
                    currentFrameRef.current,
                    FRAME_CONFIG.minFrame,
                    totalFrames
                );
            } else {
                currentFrameRef.current = clamp(
                    currentFrameRef.current,
                    FRAME_CONFIG.minFrame,
                    totalFrames - 1
                );
            }

            // ========================================================================
            // RENDER FRAME
            // ========================================================================

            const frameIndex = getFrameIndex(currentFrameRef.current);
            const texture = texturesRef.current[frameIndex];

            if (texture && spriteRef.current) {
                spriteRef.current.texture = texture;
            }

            // ========================================================================
            // TRIGGER AUDIO & VFX EVENTS
            // ========================================================================

            if (frameEvents) {
                Object.entries(frameEvents.events).forEach(([frameNumStr, event]) => {
                    const triggerFrame = parseInt(frameNumStr, 10);

                    // Check if we crossed this frame
                    if (crossedFrame(previousFrameRef.current, currentFrameRef.current, triggerFrame)) {
                        // Trigger audio
                        const audioId = `${event.type}_${frameNumStr}`;
                        console.log(`[TapeDeck] Triggering audio for frame ${frameNumStr}, audioId: ${audioId}`);
                        audioManager.play(audioId, event.audio);

                        // Trigger VFX
                        if (event.vfx.bloom.enabled && bloomRef.current) {
                            bloomRef.current.trigger(
                                event.vfx.bloom.intensity || 3.0,
                                event.vfx.bloom.duration || 0.15
                            );
                        }

                        // Trigger Emoji Burst
                        if (emojiSystemRef.current) {
                            emojiSystemRef.current.trigger(
                                currentMode,
                                app.screen.width / 2,
                                app.screen.height / 2
                            );
                        }
                    }
                });
            }
        });

        // ============================================================================
        // CLEANUP
        // ============================================================================

        return () => {
            gsap.ticker.remove(ticker);
            bloomRef.current?.dispose();
            emojiSystemRef.current?.dispose(); // Cleanup emojis
            audioManager.stopAll();
            app.destroy(true, { children: true, texture: true, baseTexture: true });
            texturesRef.current = [];
        };
    }, [frameEvents, totalFrames, framesPath, framePattern, width, height, currentMode]);

    // ============================================================================
    // RENDER
    // ============================================================================

    return (
        <div style={{ position: 'relative', width: '100%', height: '100vh', overflow: 'hidden' }}>
            <div ref={canvasRef} />

            {isLoading && (
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'white',
                    fontSize: '24px',
                    fontFamily: 'sans-serif',
                }}>
                    Loading frames...
                </div>
            )}

            {!isLoading && (
                <div style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '20px',
                    color: 'white',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    background: 'rgba(0, 0, 0, 0.5)',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    pointerEvents: 'none',
                }}>
                    <div style={{ opacity: 0.8 }}>
                        ↑ Rewind | ↓ Forward<br />
                        Tap top half ↑ | Tap bottom half ↓
                    </div>
                </div>
            )}
        </div>
    );
};
